import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search,
  Logout
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDoctor } from '../contexts/DoctorContext';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import Breadcrumbs from './Breadcrumbs';
import GlobalSearch from './GlobalSearch';
import DoctorNameDisplay from './DoctorNameDisplay';

const MainLayout = ({ children, onEditDoctor }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Gérer l'état initial de la sidebar selon la taille d'écran
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };


  const handleEditDoctor = () => {
    onEditDoctor();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar open={sidebarOpen} onToggle={handleSidebarToggle} onEditDoctor={onEditDoctor} />

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {/* AppBar */}
        <AppBar
          position="sticky"
          elevation={1}
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer - 1,
          }}
        >
          <Toolbar>
            {/* Bouton menu pour mobile */}
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleSidebarToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo et nom de la plateforme */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexGrow: 1,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.jpeg" 
                alt="AFDT Logo" 
                style={{ 
                  height: '40px', 
                  width: '40px', 
                  marginRight: '12px',
                  borderRadius: '50%'
                }} 
              />
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                AFDT-SUIVIMED
              </Typography>
            </Box>

            {/* Barre de recherche globale */}
            <Box sx={{ flexGrow: 1, maxWidth: 400, mx: 2 }}>
              {searchOpen ? (
                <GlobalSearch onClose={() => setSearchOpen(false)} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: 'grey.100',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'grey.200',
                    },
                  }}
                  onClick={handleSearchToggle}
                >
                  <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Rechercher patients, consultations...
                  </Typography>
                </Box>
              )}
            </Box>



            {/* Doctor Name Display */}
            <DoctorNameDisplay onEditDoctor={onEditDoctor} />

            {/* Bouton de déconnexion */}
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{ ml: 1 }}
              title="Se déconnecter"
            >
              <Logout />
            </IconButton>

          </Toolbar>
        </AppBar>

        {/* Contenu de la page */}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: 'background.default',
          }}
        >
          {/* Breadcrumbs */}
          <Breadcrumbs />
          
          {/* Contenu principal */}
          {children}
        </Box>
      </Box>
      
      
    </Box>
  );
};

export default MainLayout;
